import { PhoneNumber } from 'expo-contacts';
import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, TouchableOpacity } from 'react-native';

import Avatar, { AvatarProps } from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import Highlighter from '@components/HighlightWords';
import ContactGroupMemberNew from '@components/Invite/ContactGroupMemberNew';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { RestyleProps } from '@components/shared/Icon/RestyleIcon';
import { UserSearchType } from '@components/TaskList/TaskInviteOrAssignee.web';
import {
  File,
  TaskMemberRole,
  useFriendRequestMutation,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import theme from '@themes/theme';

export type ContactType = {
  firstName?: string;
  lastName?: string;
  name?: string;
  company?: string;
  phoneNumber?: string;
  isPhoneContact?: boolean;
  id: string;
  allPhoneNumbers?: PhoneNumber[];
  initials: string;
  displayName: string;
  roleLabel?: string;
  avatar?: File | null;
  avatarSize?: AvatarProps['size'];
  email?: string;
  users: any[];
  type: string;
  memberCount: number;
  friendRequestPending?: boolean;
  isSelfChat?: boolean;
  avatarUserId?: string;
  groupColorSeed?: string;
};

interface ContactProps {
  contact: ContactType;
  filterVal?: string;
  onPress: (item: ContactType) => void;
  isEditing?: boolean;
  isSelected?: boolean;
  isInviting?: boolean;
  shouldDisplayPhoneContactLabel?: boolean;
  disabled?: boolean;
  showMessageIcon?: boolean;
  onMessagePress?: (item: ContactType) => void;
  style?: RestyleProps;
  isTaskTagUser?: boolean;
  toggleFriendRequestSuccess?: (friendRequest: boolean) => void;
  isContactGroup?: boolean;
  isChat?: boolean;
  currentUserRole?: TaskMemberRole;
  openModal?: (item: ContactType) => void;
  selectedUserList?: UserSearchType[];
  disabledUsersId?: string[];
  isNewChatFromTask?: boolean;
  showEmail?: boolean;
  isSelectedBoxOnRight?: boolean;
}

const ContactNew: React.FC<ContactProps> = ({
  contact,
  onPress,
  filterVal = '',
  isEditing,
  isSelected,
  isInviting,
  shouldDisplayPhoneContactLabel = true,
  disabled = false,
  showMessageIcon = false,
  onMessagePress,
  isTaskTagUser,
  isContactGroup = false,
  isChat = false,
  toggleFriendRequestSuccess: toggleFriendRequestSuccess,
  style = { paddingHorizontal: 'm' },
  currentUserRole,
  openModal,
  selectedUserList,
  disabledUsersId,
  isNewChatFromTask = false,
  showEmail = false,
  isSelectedBoxOnRight = false,
}) => {
  const { t } = useTranslation('models');
  const { t: f } = useTranslation('format');
  const { inviteViaPhone } = useMe();

  const {
    id,
    avatar,
    phoneNumber,
    isPhoneContact,
    displayName,
    name,
    roleLabel,
    friendRequestPending,
    avatarSize = 'medium',
    isSelfChat,
    avatarUserId,
    groupColorSeed,
  } = contact;

  const [friendRequest] = useFriendRequestMutation({
    refetchQueries: ['listUsers', 'listUsersPagination'],
  });

  const onPressAdd = () => {
    toggleFriendRequestSuccess?.(statusBtn !== 'Pending');
    if (id) {
      friendRequest({
        onCompleted: () => {
          setTimeout(() => {
            toggleFriendRequestSuccess?.(false);
          }, 500);
        },
        variables: {
          attributes: {
            userIds: [id],
          },
        },
      });
    }
  };

  const [statusBtn, setStatusBtn] = useState('');
  const [isVisible, setIsVisible] = useState(true);

  const toggleVisibility = () => {
    isGroup && setIsVisible(!isVisible);
  };
  useEffect(() => {
    if (friendRequestPending) setStatusBtn('Pending');
    else setStatusBtn('Add');
  }, [friendRequestPending]);

  const isEmail = contact?.__typename === 'StringSearchType';
  const isGroup = contact?.__typename === 'Group';
  const isSelectedGroup = () => {
    if (isGroup) {
      let b = true;
      contact.users.map((u) => {
        if (
          !selectedUserList?.some((user) => u.id === user.id) &&
          !disabledUsersId?.some((user) => u.id === user)
        )
          b = false;
      });
      return b;
    }
    return false;
  };
  const disabledGroup = () => {
    if (isGroup) {
      let b = true;
      contact.users.map((u) => {
        if (!disabledUsersId?.some((user) => u.id === user)) b = false;
      });
      return b;
    }
    return false;
  };

  if (contact?.__typename === 'str') {
    return (
      <Box
        flexDirection='row'
        alignItems='center'
        justifyContent='space-between'
        {...style}>
        <Box flex={1}>
          <Text
            accessibilityLabel='Member'
            variant='body'
            numberOfLines={1}
            color='textPrimary'>
            {name}
          </Text>
        </Box>
      </Box>
    );
  }

  const notEmail = contact?.isTaskTagUser
    ? 'Already requested'
    : 'Already assignee';

  return (
    <>
      <TouchableOpacity
        testID={contact.id}
        onPress={() => onPress(contact)}
        disabled={disabled || disabledGroup() || statusBtn === 'Pending'}>
        <Box
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          {...style}>
          {isEmail && !isGroup ? (
            <EmailAvatar width={40} />
          ) : (
            !isGroup && (
              <Avatar
                avatarType={isSelfChat || !isChat ? 'user' : 'chat'}
                id={isSelfChat && avatarUserId ? avatarUserId : id}
                avatar={avatar}
                size={avatarSize}
                label={displayName ?? name}
                isMultiple={isContactGroup}
                disableAvatarPress={
                  showMessageIcon || isContactGroup || isPhoneContact
                }
                groupColorSeed={isContactGroup ? groupColorSeed : undefined}
              />
            )
          )}
          <Box marginLeft={isGroup ? undefined : 'xs'} flex={1}>
            <TouchableOpacity disabled={!isGroup} onPress={toggleVisibility}>
              <Text
                accessibilityLabel='Member'
                variant='body'
                numberOfLines={1}
                color={(displayName ?? name) ? 'textPrimary' : 'grey04'}>
                {(displayName ?? name) ? (
                  <Highlighter
                    highlightStyle={
                      Platform.OS == 'web'
                        ? {
                            backgroundColor: theme.colors.yellowBright,
                          }
                        : {
                            color: theme.colors.greenSecondary,
                            backgroundColor: theme.colors.background,
                          }
                    }
                    autoEscape
                    searchWords={filterVal?.split(' ') || ''}
                    textToHighlight={displayName ?? name}
                  />
                ) : (
                  'No name'
                )}
              </Text>
            </TouchableOpacity>
            {showEmail && !!contact?.email && (
              <Text
                variant='webBodyHintSubline'
                color='grey04'
                numberOfLines={1}>
                {contact.email}
              </Text>
            )}
            {!isNewChatFromTask && roleLabel && !disabled && (
              <TouchableOpacity
                disabled={currentUserRole !== TaskMemberRole.Owner}
                onPress={() => {
                  openModal?.(contact);
                }}>
                <Box marginTop='xxs' flexDirection='row'>
                  <Text variant='bodyTaskName' color='black'>
                    {t(roleLabel)}
                  </Text>
                  {currentUserRole === TaskMemberRole.Owner && (
                    <Icon
                      variant='s'
                      name='ChevronDown'
                      color='black'
                      marginLeft='xs'
                      onPress={() => {
                        openModal?.(contact);
                      }}
                      accessibilityLabel='RoleDropDown'
                    />
                  )}
                </Box>
              </TouchableOpacity>
            )}
            {roleLabel && disabled && (
              <Box
                mt='xxs'
                mr='m'
                alignItems='center'
                justifyContent='flex-start'
                flexDirection='row'>
                <Text variant='webBodyHintSubline' color='grey04'>
                  {roleLabel}
                </Text>
                <Box
                  mx='xxs'
                  width={4}
                  height={4}
                  borderRadius='xxxs'
                  backgroundColor='grey04'
                />
                <Text
                  variant='webBodyHintSubline'
                  color='greenSecondary'
                  flexShrink={1}
                  ellipsizeMode='tail'
                  numberOfLines={1}>
                  {isEmail ? 'Already invited' : notEmail}
                </Text>
              </Box>
            )}
            {!roleLabel && isPhoneContact && shouldDisplayPhoneContactLabel && (
              <Text
                marginTop='xxs'
                variant='bodySecondary'
                color='textSecondary'>
                From phone contact
              </Text>
            )}
            {isInviting && (
              <Text
                variant='bodySecondary'
                color='textSecondary'
                marginTop='xxs'>
                {f('phoneNumber', {
                  val: phoneNumber,
                })}
              </Text>
            )}
          </Box>
          {isEditing && !isSelectedBoxOnRight && (
            <Box justifyContent='center'>
              <RadioButton
                onPress={() => onPress?.(contact)}
                isSelected={
                  !!isSelected ||
                  isSelectedGroup() ||
                  !!selectedUserList?.some((u) => id && id === u.id)
                }
                iconVariant='m'
                disabled={disabled || disabledGroup()}
                isTaskAssignee
              />
            </Box>
          )}
          {isEditing && isSelectedBoxOnRight && (
            <Box justifyContent='center' marginLeft='xs'>
              <RadioButton
                onPress={() => onPress?.(contact)}
                isSelected={
                  !!isSelected ||
                  isSelectedGroup() ||
                  !!selectedUserList?.some((u) => id && id === u.id)
                }
                iconVariant='m'
                disabled={disabled || disabledGroup()}
                isTaskAssignee
              />
            </Box>
          )}
          {isTaskTagUser &&
            (friendRequestPending ? (
              <Box
                style={{
                  paddingHorizontal: theme.spacing.s,
                  paddingVertical: theme.spacing.xxs,
                  minWidth: 70,
                  backgroundColor: theme.colors.grey02,
                  alignItems: 'center',
                  borderRadius: theme.borderRadii.s,
                }}>
                <Text variant='buttonLabel' color='black'>
                  {statusBtn}
                </Text>
              </Box>
            ) : (
              <TouchableOpacity onPress={onPressAdd}>
                <Box
                  style={{
                    paddingHorizontal: theme.spacing.s,
                    paddingVertical: theme.spacing.xxs,
                    minWidth: 70,
                    backgroundColor: 'black',
                    alignItems: 'center',
                    borderRadius: theme.borderRadii.s,
                  }}>
                  <Text variant='buttonLabel' color='white'>
                    {statusBtn}
                  </Text>
                </Box>
              </TouchableOpacity>
            ))}
          {isPhoneContact && phoneNumber && shouldDisplayPhoneContactLabel && (
            <TouchableOpacity onPress={() => inviteViaPhone(phoneNumber)}>
              <Box
                borderColor='textPrimary'
                borderWidth={1}
                borderRadius='xs'
                paddingHorizontal='l'
                paddingVertical='xxs'>
                <Text>Invite</Text>
              </Box>
            </TouchableOpacity>
          )}
          {showMessageIcon && (
            <Icon
              name='MessageCircle'
              variant='l'
              testId='message-icon'
              marginRight='m'
              onPress={() => onMessagePress?.(contact)}
            />
          )}
        </Box>
      </TouchableOpacity>
      <ContactGroupMemberNew
        style={style}
        isVisible={isVisible}
        contact={contact}
        filterVal={filterVal}
        isEditing={isEditing}
        onPress={onPress}
        openModal={openModal}
        selectedUserList={selectedUserList}
        currentUserRole={currentUserRole}
        disabledUsersId={disabledUsersId}
      />
    </>
  );
};

export default memo(ContactNew);
